import Container from "@/components/Container/Container";
import Section from "@/components/Section/Section";
import PageHero from "@/components/PageHero/PageHero";
import CTASection from "@/components/CTASection/CTASection";
import styles from "./SupervisionPage.module.css";

const themes = [
  {
    title: "Case conceptualization",
    body: "Develop a systemic lens that helps you see the patterns and relational dynamics shaping each client's experience.",
  },
  {
    title: "Relational & somatic interventions",
    body: "Build confidence integrating somatic awareness and relational work into your clinical practice.",
  },
  {
    title: "Therapeutic presence",
    body: "Deepen your use of self, attunement, and the felt sense of being with clients.",
  },
  {
    title: "Ethical decision-making",
    body: "Work through ethical questions and complex clinical situations with thoughtful, supported reflection.",
  },
  {
    title: "Sustainable clinical identity",
    body: "Cultivate a clinical identity that's authentic, durable, and fits who you are as a therapist.",
  },
  {
    title: "Reflective practice",
    body: "Slow down to integrate experience into ongoing growth, beyond the pressure of licensure milestones.",
  },
];

const availableFor = [
  "Associate Marriage and Family Therapists (AMFTs)",
  "Therapists seeking consultation on complex cases",
  "Clinicians pursuing deeper integration of systemic approaches",
];

export default function SupervisionPage() {
  return (
    <>
      <PageHero
        eyebrow="Clinical supervision"
        title="Relational, reflective, and developmentally focused supervision."
        intro="In addition to my clinical work, I provide supervision and consultation for Associate Marriage and Family Therapists and other clinicians seeking professional growth."
        accent="bloom"
      />

      <Section spacing="default">
        <Container width="default" className={styles.philosophy}>
          <div className={styles.philosophyCopy}>
            <span className="eyebrow">My approach</span>
            <h2 className={styles.philosophyTitle}>
              A space where clinicians feel both supported and challenged.
            </h2>
            <p>
              Supervision should be a place to deepen clinical thinking, develop confident
              interventions, and grow into the therapist you want to become. I integrate
              systemic, somatic, and relational perspectives so the work stays anchored in both
              the emotional and physiological realities of healing.
            </p>
          </div>
          <aside className={styles.badge} aria-label="Supervision credential">
            <span className={styles.badgeKicker}>Aligned with</span>
            <span className={styles.badgeName}>AAMFT</span>
            <span className={styles.badgeMeta}>
              competency framework for marriage and family therapy supervision
            </span>
          </aside>
        </Container>
      </Section>

      <Section tone="soft" spacing="loose">
        <Container width="default">
          <div className={styles.sectionHeader}>
            <span className="eyebrow">What we work on</span>
            <h2>Six areas we cover together.</h2>
            <p className={styles.sectionLead}>
              Whether you're working toward licensure or building a long-term clinical practice,
              supervision can be shaped around what's most useful for where you are.
            </p>
          </div>
          <div className={styles.themesGrid}>
            {themes.map((t, idx) => (
              <article key={t.title} className={styles.themeCard}>
                <span className={styles.themeIndex}>0{idx + 1}</span>
                <h3 className={styles.themeTitle}>{t.title}</h3>
                <p>{t.body}</p>
              </article>
            ))}
          </div>
        </Container>
      </Section>

      <Section spacing="loose">
        <Container width="default" className={styles.format}>
          <div className={styles.formatCopy}>
            <span className="eyebrow">Format &amp; availability</span>
            <h2>One-on-one supervision, by inquiry.</h2>
            <p>
              Supervision is offered individually for focused, in-depth case consultation and
              personalized professional development. This format allows for sustained attention
              on clinical decision-making, skill building, and clinical identity formation.
            </p>
          </div>
          <div className={styles.availableFor}>
            <span className="eyebrow">Available for</span>
            <ul className={styles.availableList}>
              {availableFor.map((a) => (
                <li key={a}>{a}</li>
              ))}
            </ul>
          </div>
        </Container>
      </Section>

      <CTASection
        eyebrow="Interested in supervision?"
        title="Let's see if we're the right fit."
        body="Reach out to discuss your professional goals and explore whether supervision with me would support where you're headed."
      />
    </>
  );
}
